import React from 'react';
import { get, post } from '../../util/api';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	IconButton,
	TableCell,
	Table,
	TableBody,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';

// input değerleri TR local (UTC+3)
function toTRLocal(date) {
	const tr = new Date(date.getTime() + 3 * 60 * 60 * 1000);
	return tr.toISOString().slice(0, 16);
}

function getDefaultRange() {
	const now = new Date();
	const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
	return { start: toTRLocal(start), end: toTRLocal(now) };
}

// Rocket'in pid'i — default olarak HARİÇ tutulur (Rocket dışı müşteriler takip edilir)
const ROCKET_PID = 'd9c31450-5a2b-4eef-a755-6534ce76e8f3';

const SEVERITY_COLORS = {
	ok: { bg: '#e8f5e9', color: '#2e7d32', label: 'OK' },
	medium: { bg: '#fff3e0', color: '#e65100', label: 'Orta' },
	high: { bg: '#ffebee', color: '#c62828', label: 'Sorunlu' },
	unknown: { bg: '#f3e5f5', color: '#6a1b9a', label: '?' },
};

function severityColor(sev) {
	return SEVERITY_COLORS[sev] || SEVERITY_COLORS.unknown;
}

function countColor(n) {
	if (n >= 5) return { bg: '#ffebee', color: '#c62828' };
	if (n >= 3) return { bg: '#fff3e0', color: '#e65100' };
	return { bg: '#fffde7', color: '#f9a825' };
}

const ENGINE_COLORS = {
	bawa: { bg: '#e3f2fd', color: '#1565c0' },
	'bawa-sandbox': { bg: '#ede7f6', color: '#5e35b1' },
	web: { bg: '#eceff1', color: '#455a64' },
};

function engineColor(engine) {
	return ENGINE_COLORS[engine] || { bg: '#fafafa', color: '#9e9e9e' };
}

// ── Satır: canlı durum drill-down ─────────────────────────────────────────────

function LogoutRow({ row, index }) {
	const [open, setOpen] = React.useState(false);
	const [live, setLive] = React.useState(null);
	const [liveLoading, setLiveLoading] = React.useState(false);

	function fetchLive() {
		setLiveLoading(true);
		get(`/maytapi/admin/phone/${row.phone_id}/status`)
			.then((data) => setLive(data))
			.catch((e) => setLive({ error: e.message }))
			.finally(() => setLiveLoading(false));
	}

	const sc = severityColor(row.severity);
	const cc = countColor(row.logout_count);
	const ec = engineColor(row.engine);

	return (
		<React.Fragment>
			<TableRow hover>
				<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.disabled' }}>{index + 1}</TableCell>
				<TableCell sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{row.phone_id}</TableCell>
				<TableCell>
					<Chip
						label={row.logout_count}
						size="small"
						sx={{ fontSize: 12, height: 22, fontWeight: 700, backgroundColor: cc.bg, color: cc.color }}
					/>
				</TableCell>
				<TableCell>
					<Chip
						label={row.current_status || '—'}
						size="small"
						sx={{ fontSize: 11, height: 20, backgroundColor: sc.bg, color: sc.color, fontWeight: 600 }}
					/>
				</TableCell>
				<TableCell>
					{row.engine ? (
						<Chip
							label={row.engine}
							size="small"
							sx={{ fontSize: 11, height: 20, backgroundColor: ec.bg, color: ec.color, fontWeight: 600 }}
						/>
					) : (
						'—'
					)}
				</TableCell>
				<TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>{row.session_id || '—'}</TableCell>
				<TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.number || '—'}</TableCell>
				<TableCell sx={{ fontSize: 12 }}>{row.conclusion}</TableCell>
				<TableCell sx={{ fontSize: 12 }}>
					{row.email ? (
						<a href={'/yta/logs?uid=' + row.uid} target="_blank" rel="noopener noreferrer">
							{row.email}
						</a>
					) : (
						<span style={{ color: '#999', fontFamily: 'monospace', fontSize: 11 }} title={row.pid}>
							{(row.pid || '').slice(0, 8)}
						</span>
					)}
				</TableCell>
				<TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
					{row.updated_at ? moment(row.updated_at).format('DD-MM HH:mm') : '—'}
				</TableCell>
				<TableCell sx={{ p: 0.5, width: 36 }}>
					<IconButton
						size="small"
						onClick={() => {
							setOpen((o) => !o);
							if (!open && live === null) fetchLive();
						}}
					>
						{open ? <ExpandMoreIcon fontSize="small" /> : <MoreVertIcon fontSize="small" />}
					</IconButton>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell colSpan={11} sx={{ p: 0, border: open ? undefined : 0 }}>
					<Collapse in={open} unmountOnExit>
						<Box sx={{ p: 1.5, backgroundColor: '#282c34', borderBottom: '1px solid #e0e0e0' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
								<Button
									size="small"
									variant="outlined"
									color="info"
									startIcon={liveLoading ? <CircularProgress size={12} /> : <RefreshIcon fontSize="small" />}
									onClick={fetchLive}
									disabled={liveLoading}
									sx={{ textTransform: 'none', fontSize: 11 }}
								>
									Canlı Durum (status)
								</Button>
								<Typography variant="caption" sx={{ color: '#abb2bf' }}>
									pid: {row.pid}
								</Typography>
							</Box>
							<Box
								component="pre"
								sx={{
									m: 0,
									fontFamily: 'monospace',
									fontSize: 12,
									color: '#98c379',
									maxHeight: 260,
									overflow: 'auto',
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word',
								}}
							>
								{liveLoading ? 'yükleniyor...' : live ? JSON.stringify(live, null, 2) : 'Canlı durum için butona bas.'}
							</Box>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</React.Fragment>
	);
}

// ── Sayfa ─────────────────────────────────────────────────────────────────────

export default function LogoutTracking() {
	const defaults = getDefaultRange();
	const [rangeStart, setRangeStart] = React.useState(defaults.start);
	const [rangeEnd, setRangeEnd] = React.useState(defaults.end);
	const [minCount, setMinCount] = React.useState(2);
	const [pid, setPid] = React.useState('');
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState(null);
	const [result, setResult] = React.useState(null);
	const [alerting, setAlerting] = React.useState(false);
	const [alertMsg, setAlertMsg] = React.useState(null);

	const toUtc = (trLocal) => new Date(trLocal + ':00+03:00').toISOString();

	function analyze() {
		setLoading(true);
		setError(null);
		get('/maytapi/admin/logoutTracking', {
			rangeStart: toUtc(rangeStart),
			rangeEnd: toUtc(rangeEnd),
			minCount,
			pid: pid && pid.trim() ? pid.trim() : undefined, // boş → backend default: Rocket hariç
		})
			.then((data) => {
				if (data.success) setResult(data);
				else setError(data.message || 'Analiz başarısız');
			})
			.catch((err) => setError(err.message || 'İstek başarısız'))
			.finally(() => setLoading(false));
	}

	// Ekranda GÖRÜNEN listeyi olduğu gibi alert olarak gönderir (WYSIWYG).
	function sendAlert() {
		if (!result || !result.list) {
			setAlertMsg('ℹ️ Önce "Analiz Et" ile veriyi getir.');
			return;
		}
		setAlerting(true);
		setAlertMsg(null);
		const windowLabel = result.range
			? `analiz: ${moment(result.range.from).format('DD.MM HH:mm')}–${moment(result.range.to).format('DD.MM HH:mm')}`
			: undefined;
		post('/maytapi/admin/logoutAlert/send', {
			list: result.list,
			windowLabel,
		})
			.then((resp) => {
				const data = resp.data || resp;
				if (!data.success) setAlertMsg('❌ Hata: ' + (data.message || 'bilinmiyor'));
				else if (data.sent) setAlertMsg(`✅ Gönderildi: ${data.count} telefon → ${data.to}`);
				else setAlertMsg('ℹ️ ' + (data.reason || 'Aktif olmayan telefon yok, mesaj atılmadı'));
			})
			.catch((err) => setAlertMsg('❌ Hata: ' + err.message))
			.finally(() => setAlerting(false));
	}

	const stats = React.useMemo(() => {
		if (!result || !result.list) return null;
		const s = { total: result.list.length, high: 0, medium: 0, ok: 0 };
		result.list.forEach((r) => {
			if (r.severity === 'high' || r.severity === 'unknown') s.high++;
			else if (r.severity === 'medium') s.medium++;
			else if (r.severity === 'ok') s.ok++;
		});
		return s;
	}, [result]);

	return (
		<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
			<Typography variant="h6">Logout Takip</Typography>
			<Typography variant="caption" color="text.secondary">
				Seçilen aralıkta <strong>{minCount}+</strong> kez logout olan telefonları listeler ve şu anki durumlarını çeker. Default:{' '}
				<strong>Rocket hariç</strong> (diğer tüm müşteriler). Tek müşteri için pid yaz, hepsi için <code>all</code>.
			</Typography>

			<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
				<TextField
					label="Başlangıç (TR)"
					type="datetime-local"
					value={rangeStart}
					onChange={(e) => setRangeStart(e.target.value)}
					size="small"
					InputLabelProps={{ shrink: true }}
				/>
				<TextField
					label="Bitiş (TR)"
					type="datetime-local"
					value={rangeEnd}
					onChange={(e) => setRangeEnd(e.target.value)}
					size="small"
					InputLabelProps={{ shrink: true }}
				/>
				<TextField
					label="Min logout"
					type="number"
					value={minCount}
					onChange={(e) => setMinCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
					size="small"
					sx={{ width: 110 }}
					InputLabelProps={{ shrink: true }}
				/>
				<TextField
					label="pid (boş = Rocket hariç, 'all' = tümü)"
					placeholder="boş bırak → Rocket hariç"
					value={pid}
					onChange={(e) => setPid(e.target.value)}
					size="small"
					sx={{ width: 320 }}
					InputLabelProps={{ shrink: true }}
				/>
				<Button
					variant="contained"
					startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
					onClick={analyze}
					disabled={loading}
				>
					Analiz Et
				</Button>
				<Button
					variant="outlined"
					color="warning"
					startIcon={alerting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
					onClick={sendAlert}
					disabled={alerting || !result}
				>
					Alert Gönder (test)
				</Button>
			</Box>

			<Typography variant="caption" color="text.secondary">
				"Alert Gönder": <strong>ekranda görünen</strong> listedeki <strong>şu an aktif olmayan</strong> telefonları WhatsApp mesajı olarak atar
				(şimdilik test numarasına). Önce "Analiz Et" ile veriyi getir. Saatlik cron ise veriyi kendi çekip aynısını otomatik yapar.
			</Typography>

			{alertMsg && (
				<Typography variant="body2" sx={{ fontWeight: 600 }}>
					{alertMsg}
				</Typography>
			)}

			{error && (
				<Typography color="error" variant="body2">
					{error}
				</Typography>
			)}

			{stats && (
				<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
					<Chip label={`Toplam: ${stats.total}`} size="small" />
					<Chip label={`Sorunlu: ${stats.high}`} size="small" sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }} />
					<Chip label={`Orta: ${stats.medium}`} size="small" sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
					<Chip label={`OK: ${stats.ok}`} size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
				</Box>
			)}

			{result && (
				<Box sx={{ overflowX: 'auto' }}>
					<Table size="small" sx={{ minWidth: 900 }}>
						<TableHead>
							<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
								<TableCell sx={{ fontWeight: 'bold', width: 40, color: 'text.disabled' }}>#</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>phone_id</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Logout</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Şu anki durum</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Engine</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Session</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Numara</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Çıkarım</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Müşteri</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>Güncelleme</TableCell>
								<TableCell sx={{ width: 36 }} />
							</TableRow>
						</TableHead>
						<TableBody>
							{result.list.length === 0 ? (
								<TableRow>
									<TableCell colSpan={11}>
										<Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
											Bu aralıkta {minCount}+ logout olan telefon yok.
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								result.list.map((row, i) => <LogoutRow key={`${row.phone_id}-${row.pid}`} row={row} index={i} />)
							)}
						</TableBody>
					</Table>
				</Box>
			)}
		</Box>
	);
}
